import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { LiveTrainingCoachTablesDialog } from "../LiveTraining/LiveTrainingCoachTablesDialog";
import { useCoachSessionTables } from "../../hooks/useCoachSessionTables";
import {
  copyTableSetup,
  sessionexlist,
} from "../../components/funtionsBD/Mainservice";
import { fetchSessionTablesDeduped } from "../../utils/sessionTableFetch";
import {
  countCoachTablesWithBothPlayers,
  filterCoachConfiguredTables,
  filterPresentRoster,
  getAssignedPlayerIds,
  getUnassignedPresentPlayers,
  normalizeSessionTablesForCoachSetup,
  previousExerciseForSeatingCopy,
  tablesHaveSeatingSetup,
} from "../../utils/coachSessionTables";
import { isTableConfigurationLockedAfterFinalize } from "../../utils/sessionPresenceEdit";
import { userCanEditFinalizedExerciseSeating } from "../../utils/planningAccess";
import { resolveTableGrId } from "../../utils/sessionResultsPlayers";
import {
  resolveExerciseStateIdFromRow,
  TE_STATE_FINALIZED,
} from "../../utils/coachExerciseNavigation";
import { isOffTableExercise } from "../../utils/teExecutionType";

/**
 * Staff seating editor for a finalized session exercise (outside Live Training).
 * Optional "Copy last exercise seating" when the previous block has table setup.
 */
export function FinalizedExerciseSeatingDialog({
  open,
  onClose,
  session = null,
  selectedgrp = null,
  grId: grIdProp = null,
  exercise = null,
  roster = [],
  user = null,
}) {
  const { t } = useTranslation();
  const tsId = session?.ts_id || null;
  const teId = exercise?.te_id || null;

  const grId = useMemo(
    () =>
      grIdProp ||
      resolveTableGrId(session, null, selectedgrp) ||
      selectedgrp?.groupDetails?.gr_id ||
      session?.gr_id ||
      null,
    [grIdProp, session, selectedgrp]
  );

  const canEdit = userCanEditFinalizedExerciseSeating(user);
  const [exercises, setExercises] = useState([]);
  const [exercisesLoading, setExercisesLoading] = useState(false);
  const [copyBusy, setCopyBusy] = useState(false);
  const [prevHasSeating, setPrevHasSeating] = useState(false);
  const [prevCheckBusy, setPrevCheckBusy] = useState(false);

  // Results roster often loses `presensee` during normalization. For staff
  // correction of a finalized exercise, every listed participant is seatable.
  const seatingRoster = useMemo(() => {
    const source = Array.isArray(roster) ? roster : [];
    const markedPresent = source.map((p) =>
      p && typeof p === "object" ? { ...p, presensee: true } : p
    );
    const presentOnly = filterPresentRoster(markedPresent);
    return presentOnly.length ? presentOnly : markedPresent;
  }, [roster]);

  useEffect(() => {
    if (!open || !tsId) {
      setExercises([]);
      return undefined;
    }
    let cancelled = false;
    setExercisesLoading(true);
    (async () => {
      try {
        const response = await sessionexlist({ ts_id: tsId });
        const list = response?.data?.data || response?.data || [];
        if (!cancelled) {
          setExercises(Array.isArray(list) ? list : []);
        }
      } catch {
        if (!cancelled) setExercises([]);
      } finally {
        if (!cancelled) setExercisesLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, tsId]);

  const previousExercise = useMemo(
    () => previousExerciseForSeatingCopy(exercises, teId),
    [exercises, teId]
  );

  useEffect(() => {
    if (!open || !grId || !tsId || !previousExercise?.te_id) {
      setPrevHasSeating(false);
      return undefined;
    }
    let cancelled = false;
    setPrevCheckBusy(true);
    (async () => {
      try {
        const res = await fetchSessionTablesDeduped({
          gr_id: grId,
          ts_id: tsId,
          te_id: previousExercise.te_id,
        });
        const raw = res?.data?.data || res?.data || [];
        const tables = normalizeSessionTablesForCoachSetup(
          Array.isArray(raw) ? raw : []
        );
        if (!cancelled) {
          setPrevHasSeating(tablesHaveSeatingSetup(tables));
        }
      } catch {
        if (!cancelled) setPrevHasSeating(false);
      } finally {
        if (!cancelled) setPrevCheckBusy(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, grId, tsId, previousExercise?.te_id]);

  const tableSetup = useCoachSessionTables({
    grId,
    tsId,
    teId,
    roster: seatingRoster,
    exercise,
    exercises,
    socket: null,
    enabled: Boolean(open && grId && tsId && teId && canEdit),
  });

  useEffect(() => {
    if (!open || !tableSetup?.error) return;
    window.alert(tableSetup.error);
  }, [open, tableSetup?.error]);

  const exerciseStateId = exercise
    ? resolveExerciseStateIdFromRow(exercise)
    : null;
  const isFinalized = exerciseStateId === TE_STATE_FINALIZED;
  const tableConfigurationLocked = isTableConfigurationLockedAfterFinalize({
    isFinalized,
    canEditFinalizedSeating: canEdit,
  });
  const seatingEnabled = Boolean(canEdit && !tableConfigurationLocked && grId);

  const unseatedPlayers = useMemo(
    () =>
      getUnassignedPresentPlayers(
        seatingRoster,
        tableSetup.stationRawTables?.length
          ? tableSetup.stationRawTables
          : tableSetup.rawTables
      ),
    [seatingRoster, tableSetup.stationRawTables, tableSetup.rawTables]
  );

  const assignedCount = useMemo(() => {
    const tables =
      tableSetup.stationRawTables?.length
        ? tableSetup.stationRawTables
        : tableSetup.rawTables;
    return getAssignedPlayerIds(tables || []).size;
  }, [tableSetup.stationRawTables, tableSetup.rawTables]);

  const usedTableCount = useMemo(
    () => countCoachTablesWithBothPlayers(tableSetup.tables || []),
    [tableSetup.tables]
  );
  const roomCapacity = useMemo(
    () => filterCoachConfiguredTables(tableSetup.tables || []).length,
    [tableSetup.tables]
  );

  const currentHasSeating = assignedCount > 0;

  const copyLastDisabledTitle = !previousExercise
    ? t("copyLastExerciseSeatingNoPrevious") ||
      "No previous exercise to copy from"
    : !prevHasSeating
      ? t("copyLastExerciseSeatingDisabled") ||
        "Previous exercise has no table seating"
      : null;

  const handleCopyLastSeating = useCallback(async () => {
    if (
      !tsId ||
      !teId ||
      !previousExercise?.te_id ||
      !prevHasSeating ||
      copyBusy
    ) {
      return;
    }
    if (currentHasSeating) {
      const ok = window.confirm(
        t("copyLastExerciseSeatingConfirm") ||
          "Replace this exercise’s seating with the previous exercise’s table setup?"
      );
      if (!ok) return;
    }
    setCopyBusy(true);
    try {
      const res = await copyTableSetup({
        from_te_id: previousExercise.te_id,
        to_te_id: teId,
        ts_id: tsId,
      });
      const copied = res?.data?.data?.copied ?? res?.data?.copied ?? 0;
      if (!copied) {
        window.alert(
          t("no_table_setup_to_copy") || "No table setup found to copy"
        );
      }
      await tableSetup.reload?.();
    } catch (err) {
      window.alert(
        err?.response?.data?.message ||
          err?.message ||
          t("error") ||
          "Could not copy seating"
      );
    } finally {
      setCopyBusy(false);
    }
  }, [
    tsId,
    teId,
    previousExercise?.te_id,
    prevHasSeating,
    copyBusy,
    currentHasSeating,
    t,
    tableSetup,
  ]);

  if (!open || !exercise || isOffTableExercise(exercise)) {
    return null;
  }

  return (
    <LiveTrainingCoachTablesDialog
      open={open}
      onClose={onClose}
      tableSetup={tableSetup}
      seatingActionsEnabled={seatingEnabled}
      exerciseLocked={!seatingEnabled}
      roster={seatingRoster}
      presentPlayers={seatingRoster}
      unseatedPlayers={unseatedPlayers}
      assignedCount={assignedCount}
      presentCount={seatingRoster.length}
      usedTableCount={usedTableCount}
      roomCapacity={roomCapacity || tableSetup.tables?.length || 0}
      activeTables={[]}
      loading={tableSetup.loading || exercisesLoading}
      tsId={tsId}
      teId={teId}
      grId={grId}
      exercise={exercise}
      exerciseStateId={exerciseStateId}
      user={user}
      showCopyLastSeating
      copyLastSeatingAvailable={Boolean(previousExercise && prevHasSeating)}
      copyLastSeatingBusy={copyBusy || prevCheckBusy}
      onCopyLastSeating={handleCopyLastSeating}
      copyLastSeatingDisabledTitle={copyLastDisabledTitle}
      elevated
    />
  );
}

export default FinalizedExerciseSeatingDialog;
